import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, Send } from 'lucide-react';
import client from '../../api/client';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

function getMonday(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function fmt(date) {
  return date.toISOString().slice(0, 10);
}

function fmtShort(date) {
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

const STATUS_LABELS = {
  draft: 'Borrador',
  submitted: 'Enviado',
  approved: 'Aprobado',
  returned: 'Devuelto',
};
const STATUS_COLORS = {
  draft: { bg: '#f3e8ff', color: '#7c3aed' },
  submitted: { bg: '#fef9c3', color: '#ca8a04' },
  approved: { bg: '#dcfce7', color: '#16a34a' },
  returned: { bg: '#fed7aa', color: '#ea580c' },
};

export default function MyTimesheetPage() {
  const navigate = useNavigate();
  const [monday, setMonday] = useState(getMonday());
  const [timesheet, setTimesheet] = useState(null);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]); // { projectId, hours: [0,0,0,0,0], descriptions: ['','','','',''] }
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const weekDates = Array.from({ length: 5 }, (_, i) => addDays(monday, i));

  const fetchWeek = useCallback(async (mon) => {
    setLoading(true);
    try {
      const [tsRes, projRes] = await Promise.all([
        client.get('/api/timesheets/current', { params: { week: fmt(mon) } }),
        client.get('/api/projects'),
      ]);
      setTimesheet(tsRes.data);
      setProjects(projRes.data.filter(p => p.active !== false));

      // Build rows from entries
      const entries = tsRes.data.entries || [];
      const projectMap = {};
      entries.forEach(e => {
        const pId = e.project_id || 0;
        if (!projectMap[pId]) {
          projectMap[pId] = { projectId: pId, hours: [0,0,0,0,0], descriptions: ['','','','',''] };
        }
        const dayIdx = weekDates.findIndex(d => fmt(d) === e.entry_date);
        if (dayIdx >= 0) {
          projectMap[pId].hours[dayIdx] = parseFloat(e.hours) || 0;
          projectMap[pId].descriptions[dayIdx] = e.description || '';
        }
      });

      const rowsArr = Object.values(projectMap);
      if (rowsArr.length === 0) {
        // Start with one empty row
        rowsArr.push({ projectId: 0, hours: [0,0,0,0,0], descriptions: ['','','','',''] });
      }
      setRows(rowsArr);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWeek(monday); }, [monday, fetchWeek]);

  function handlePrev() { setMonday(addDays(monday, -7)); }
  function handleNext() { setMonday(addDays(monday, 7)); }
  function handleToday() { setMonday(getMonday()); }

  function addRow() {
    setRows([...rows, { projectId: 0, hours: [0,0,0,0,0], descriptions: ['','','','',''] }]);
  }

  function removeRow(idx) {
    setRows(rows.filter((_, i) => i !== idx));
  }

  function updateProject(idx, projectId) {
    const newRows = [...rows];
    newRows[idx] = { ...newRows[idx], projectId: parseInt(projectId) || 0 };
    setRows(newRows);
  }

  function updateHours(rowIdx, dayIdx, val) {
    const newRows = [...rows];
    newRows[rowIdx] = { ...newRows[rowIdx], hours: [...newRows[rowIdx].hours] };
    newRows[rowIdx].hours[dayIdx] = parseFloat(val) || 0;
    setRows(newRows);
  }

  function updateDescription(rowIdx, dayIdx, val) {
    const newRows = [...rows];
    newRows[rowIdx] = { ...newRows[rowIdx], descriptions: [...newRows[rowIdx].descriptions] };
    newRows[rowIdx].descriptions[dayIdx] = val;
    setRows(newRows);
  }

  // Flatten rows into entries array
  function buildEntries() {
    const entries = [];
    rows.forEach(row => {
      weekDates.forEach((date, dayIdx) => {
        if (row.hours[dayIdx] > 0) {
          entries.push({
            project_id: row.projectId || null,
            entry_date: fmt(date),
            hours: row.hours[dayIdx],
            description: row.descriptions[dayIdx] || null,
          });
        }
      });
    });
    return entries;
  }

  async function handleSave() {
    if (!timesheet) return;
    setSaving(true);
    try {
      const res = await client.post('/api/timesheets/entries', {
        timesheetId: timesheet.id,
        entries: buildEntries(),
      });
      setTimesheet(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (!timesheet) return;
    // Save first, then submit
    setSubmitting(true);
    try {
      await client.post('/api/timesheets/entries', {
        timesheetId: timesheet.id,
        entries: buildEntries(),
      });
      const res = await client.post(`/api/timesheets/${timesheet.id}/submit`);
      setTimesheet(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al enviar.');
    } finally {
      setSubmitting(false);
    }
  }

  const isEditable = timesheet && (timesheet.status === 'draft' || timesheet.status === 'returned');
  const totalHours = rows.reduce((sum, r) => sum + r.hours.reduce((a, b) => a + b, 0), 0);
  const dayTotals = weekDates.map((_, dayIdx) => rows.reduce((sum, r) => sum + (r.hours[dayIdx] || 0), 0));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  const statusCfg = STATUS_COLORS[timesheet?.status] || STATUS_COLORS.draft;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Mi Timesheet</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            padding: '3px 12px', borderRadius: 12,
            background: statusCfg.bg, color: statusCfg.color,
            fontSize: 12, fontWeight: 600,
          }}>
            {STATUS_LABELS[timesheet?.status] || 'Borrador'}
          </span>
          <button onClick={() => navigate('/horas/historial')} style={{
            background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
            borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#64748b',
            cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>
            Historial
          </button>
        </div>
      </div>

      {/* Reviewer notes (if returned) */}
      {timesheet?.status === 'returned' && timesheet?.reviewer_notes && (
        <div style={{
          ...bubbleStyle, padding: '12px 18px', marginBottom: 16,
          borderLeft: '3px solid #ea580c', background: '#fff7ed',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#ea580c', marginBottom: 4 }}>
            Notas del revisor:
          </div>
          <div style={{ fontSize: 13, color: '#9a3412' }}>{timesheet.reviewer_notes}</div>
        </div>
      )}

      {/* Week navigator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
        justifyContent: 'center',
      }}>
        <button onClick={handlePrev} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY }}>
          <ChevronLeft size={20} />
        </button>
        <span style={{ fontSize: 14, fontWeight: 600, color: NAVY, minWidth: 180, textAlign: 'center' }}>
          {fmtShort(weekDates[0])} — {fmtShort(weekDates[4])}
        </span>
        <button onClick={handleNext} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY }}>
          <ChevronRight size={20} />
        </button>
        <button onClick={handleToday} style={{
          fontSize: 11, padding: '3px 10px', borderRadius: 4,
          background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`,
          cursor: 'pointer', color: '#64748b', fontFamily: 'Inter, sans-serif',
        }}>
          Hoy
        </button>
      </div>

      {/* Timesheet grid */}
      <div style={{ ...bubbleStyle, padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, minWidth: 160 }}>
                Proyecto
              </th>
              {weekDates.map((d, i) => (
                <th key={i} style={{
                  textAlign: 'center', padding: '10px 6px', fontWeight: 600, color: NAVY,
                  borderBottom: `1px solid ${BORDER_COLOR}`, minWidth: 70,
                }}>
                  <div>{DAYS[i]}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{fmtShort(d)}</div>
                </th>
              ))}
              <th style={{
                textAlign: 'center', padding: '10px 8px', fontWeight: 700, color: NAVY,
                borderBottom: `1px solid ${BORDER_COLOR}`, minWidth: 50,
              }}>
                Total
              </th>
              {isEditable && <th style={{ width: 36, borderBottom: `1px solid ${BORDER_COLOR}` }} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const rowTotal = row.hours.reduce((a, b) => a + b, 0);
              return (
                <tr key={rowIdx} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  <td style={{ padding: '8px 12px' }}>
                    {isEditable ? (
                      <select
                        value={row.projectId}
                        onChange={e => updateProject(rowIdx, e.target.value)}
                        style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12 }}
                      >
                        <option value={0}>— Sin proyecto —</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: 12, color: NAVY }}>
                        {projects.find(p => p.id === row.projectId)?.name || 'Sin proyecto'}
                      </span>
                    )}
                  </td>
                  {weekDates.map((_, dayIdx) => (
                    <td key={dayIdx} style={{ textAlign: 'center', padding: '6px 4px' }}>
                      {isEditable ? (
                        <input
                          type="number"
                          value={row.hours[dayIdx] || ''}
                          onChange={e => updateHours(rowIdx, dayIdx, e.target.value)}
                          min={0} max={24} step={0.25}
                          placeholder="0"
                          title="Click derecho para agregar descripción"
                          style={{
                            ...fieldStyle, width: 52, textAlign: 'center',
                            padding: '5px 4px', fontSize: 13, margin: '0 auto',
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 13, color: row.hours[dayIdx] > 0 ? NAVY : '#cbd5e1' }}>
                          {row.hours[dayIdx] || '—'}
                        </span>
                      )}
                    </td>
                  ))}
                  <td style={{
                    textAlign: 'center', padding: '8px', fontWeight: 700,
                    color: rowTotal > 0 ? NAVY : '#cbd5e1', fontSize: 13,
                  }}>
                    {rowTotal.toFixed(1)}
                  </td>
                  {isEditable && (
                    <td style={{ padding: '4px' }}>
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(rowIdx)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ef4444', padding: 4,
                        }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {/* Day totals row */}
            <tr style={{ background: '#f8fafc' }}>
              <td style={{ padding: '10px 12px', fontWeight: 700, color: NAVY, fontSize: 12 }}>
                Total Diario
              </td>
              {dayTotals.map((dt, i) => (
                <td key={i} style={{
                  textAlign: 'center', padding: '10px 6px', fontWeight: 700,
                  color: dt > 8 ? '#ef4444' : dt > 0 ? NAVY : '#cbd5e1', fontSize: 13,
                }}>
                  {dt.toFixed(1)}
                </td>
              ))}
              <td style={{
                textAlign: 'center', padding: '10px 8px', fontWeight: 800,
                color: AMBER_DARK, fontSize: 14,
              }}>
                {totalHours.toFixed(1)}
              </td>
              {isEditable && <td />}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Actions */}
      {isEditable && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <button onClick={addRow} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
            borderRadius: 6, padding: '7px 14px', fontSize: 12, color: '#64748b',
            cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>
            <Plus size={14} /> Agregar Proyecto
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{
              ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
              background: '#64748b', padding: '8px 16px', fontSize: 13,
              opacity: saving ? 0.6 : 1,
            }}>
              <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={handleSubmit} disabled={submitting} style={{
              ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', fontSize: 13,
              opacity: submitting ? 0.6 : 1,
            }}>
              <Send size={14} /> {submitting ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
